import { Alert, Box, Button, Grid, TextField, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { ComplianceIndicators, getComplianceIndicators, updateComplianceIndicators } from "src/apis/upsell";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { formatDateToBR } from "src/utils/date";
import { Id } from "src/utils/types";

const fields: { key: keyof Omit<ComplianceIndicators, "company_id" | "updated_at" | "updated_by">; label: string; helperText?: string }[] = [
  { key: "coverage_percentage", label: "Cobertura de atendimentos (%)" },
  { key: "monthly_average", label: "Média de atendimentos/mês" },
  { key: "yearly_total", label: "Total de atendimentos no ano" },
  { key: "lectures_count", label: "Palestras realizadas" },
  { key: "dynamics_count", label: "Dinâmicas aplicadas" },
  { key: "trainings_completed", label: "Treinamentos concluídos" },
];

type ComplianceIndicatorsTabProps = {
  companyId: Id;
};

const ComplianceIndicatorsTab: FC<ComplianceIndicatorsTabProps> = ({ companyId }) => {
  const [values, setValues] = useState<ComplianceIndicators | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const fetchIndicators = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError("");
    try {
      const response = await getComplianceIndicators(companyId);
      setValues(response);
    } catch (err: any) {
      setError(err?.message || "Erro ao carregar indicadores");
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  const handleChange = (key: keyof ComplianceIndicators, value: string) => {
    setValues((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: value === "" ? 0 : Number(value),
      } as ComplianceIndicators;
    });
  };

  const handleSave = async () => {
    if (!companyId || !values) return;

    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateComplianceIndicators(companyId, {
        coverage_percentage: values.coverage_percentage,
        monthly_average: values.monthly_average,
        yearly_total: values.yearly_total,
        lectures_count: values.lectures_count,
        dynamics_count: values.dynamics_count,
        trainings_completed: values.trainings_completed,
      });
      setSuccess("Indicadores atualizados com sucesso!");
    } catch (err: any) {
      setError(err?.message || "Erro ao salvar indicadores");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {isLoading || !values ? (
        <SkeletonLoad variant="form" rows={6} />
      ) : (
        <>
          <Typography>
            Última atualização {values.updated_at ? `em ${formatDateToBR(values.updated_at)}` : "ainda não realizada"}.
          </Typography>
          <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid item xs={12} md={4} key={field.key as string}>
                <TextField
                  fullWidth
                  type="number"
                  label={field.label}
                  value={values[field.key] ?? 0}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  helperText={field.helperText}
                />
              </Grid>
            ))}
          </Grid>
          <Box display="flex" gap={2}>
            <Button variant="contained" disabled={isSaving} onClick={handleSave}>
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
            <Button variant="outlined" disabled={isSaving} onClick={fetchIndicators}>
              Recarregar indicadores
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ComplianceIndicatorsTab;
